import { createSelector } from '@reduxjs/toolkit'
import { AccountDetail, externalInvestmentTypes } from '@repo/accounts-types'
import { EXTERNAL_TOOL } from '@repo/shared-types'
import { individualPortfolioSelector } from './individualPortfolio.selector'
import { accountDetailDataSelector } from './selectors'

const hasAnyProductsContracted = (details: AccountDetail['details'] | undefined): boolean => {
  if (!details) return false

  return Object.values(details).some((section) => (section?.items?.length ?? 0) > 0)
}

const hasExternalProductsInAccount = (details: AccountDetail['details'] | undefined): boolean => {
  if (!details) return false

  return externalInvestmentTypes.some((type) => details[type]?.items.length)
}

const DYNAMIC_PERMISSION_IDS: Array<EXTERNAL_TOOL> = [
  EXTERNAL_TOOL.VIEW_PORTFOLIO_PERFORMANCE,
  EXTERNAL_TOOL.VIEW_FINANCIAL_OPERATIONS,
  EXTERNAL_TOOL.MANAGE_AUTHORIZED_USERS,
  EXTERNAL_TOOL.VIEW_ORDERS,
]

const haveContractedProductsSelector = createSelector(
  [accountDetailDataSelector.isInitialLoadingSelector, accountDetailDataSelector.payloadSelector],
  (isLoading, accountDetail) => {
    if (isLoading) return true

    return hasAnyProductsContracted(accountDetail?.data?.details)
  },
)

const canDoSelector = createSelector([haveContractedProductsSelector], (haveContractedProducts) => {
  // TODO: añadir la logica cuando eshabilitar todos los botones cuando se añada el filtro de historicos
  const permissions = {
    [EXTERNAL_TOOL.VIEW_TRANSACTIONS]: true,
  }
  const dynamicPermissions = DYNAMIC_PERMISSION_IDS.reduce(
    (permissions, permissionId) => {
      return {
        ...permissions,
        [permissionId]: haveContractedProducts,
      }
    },
    {} as Partial<Record<EXTERNAL_TOOL, boolean>>,
  )

  return {
    ...permissions,
    ...dynamicPermissions,
  }
})

const accountHasExternalProductsSelector = createSelector(
  [accountDetailDataSelector.isInitialLoadingSelector, accountDetailDataSelector.payloadSelector],
  (isLoading, accountDetail) => {
    if (isLoading) return false

    return hasExternalProductsInAccount(accountDetail?.data?.details)
  },
)

export const accountsSelector = createSelector(
  [
    individualPortfolioSelector,
    haveContractedProductsSelector,
    canDoSelector,
    accountHasExternalProductsSelector,
  ],
  (
    { isLoading, accountDetail, meta, hasError },
    haveContractedProducts,
    canDo,
    accountHasExternalProducts,
  ) => {
    return {
      isLoading,
      accountDetail,
      haveContractedProducts,
      canDo,
      meta,
      hasError,
      accountHasExternalProducts,
    }
  },
)
